import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Power, Zap } from "lucide-react";
import { Panel, Chip, Kpi } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, gbp, fmt, ldnTime } from "@/lib/api";

export function BrokerSummary({ broker }) {
  const b = broker || {};
  const ok = b.status === "connected";
  return (
    <div className="space-y-2 text-sm" data-testid="broker-summary">
      <div className="flex flex-wrap items-center gap-2">
        <Chip status={ok ? "ON" : b.configured ? "WARNING" : "OFF"} label={ok ? "MT5 connected" : b.configured ? `MT5 ${b.status}` : "MT5 not linked"} testid="broker-status-chip" />
        {b.environment && b.configured && <Chip status={b.environment === "live" ? "LIVE" : "PAPER"} label={`${b.environment} account`} />}
      </div>
      {b.account && <div className="num text-slate-300" data-testid="broker-account-line">{b.account.name}: {fmt(b.account.balance)} {b.account.currency}, equity {fmt(b.account.equity)}</div>}
      {!b.configured && <p className="text-slate-500">Link your MT5 account in <Link to="/settings" className="text-indigo-300 underline" data-testid="broker-settings-link">Settings</Link> to receive prices and mirror trades.</p>}
    </div>
  );
}

function KillConfig({ kill, onSaved }) {
  const [f, setF] = useState(kill);
  useEffect(() => { setF(kill); }, [kill]);
  const save = async () => {
    try { await api.put("/levels/kill-config", { enabled: f.enabled, kill_loss_pct: Number(f.kill_loss_pct), kill_warning_minutes: Number(f.kill_warning_minutes), daily_rebase: !!f.daily_rebase, rebase_time: f.rebase_time || "00:55" }); toast.success("Kill switch saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="kill-enabled-switch" checked={!!f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} />Enabled</label>
      <label className="text-xs text-slate-400">Loss %<Input data-testid="kill-loss-pct-input" value={f.kill_loss_pct} onChange={(e) => setF({ ...f, kill_loss_pct: e.target.value })} className="num h-8 w-20 bg-slate-950" /></label>
      <label className="text-xs text-slate-400">Warning minutes<Input data-testid="kill-warning-minutes-input" value={f.kill_warning_minutes} onChange={(e) => setF({ ...f, kill_warning_minutes: e.target.value })} className="num h-8 w-20 bg-slate-950" /></label>
      <label className="flex items-center gap-2 text-sm text-slate-300" title="While armed, the baseline equity is reset to the current MT5 equity at this London time each weekday, the same as re-arming each morning."><Switch data-testid="kill-daily-rebase-switch" checked={!!f.daily_rebase} onCheckedChange={(v) => setF({ ...f, daily_rebase: v })} />Reset baseline daily at</label>
      <Input data-testid="kill-rebase-time-input" value={f.rebase_time || "00:55"} onChange={(e) => setF({ ...f, rebase_time: e.target.value })} className="num h-8 w-20 bg-slate-950" />
      <Button data-testid="kill-config-save" size="sm" variant="secondary" onClick={save}>Save</Button>
    </div>
  );
}

export function LivePanel({ cfg, broker, isAdmin, onChange }) {
  const k = cfg?.kill || {};
  const act = async (path, msg) => {
    try { await api.post(path); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Panel title="MT5 live mirroring" testid="levels-live-card" right={<Chip status={cfg?.armed ? "ARMED" : "PAPER"} label={cfg?.armed ? "LIVE · ARMED" : "PAPER"} testid="levels-armed-badge" />}>
        <BrokerSummary broker={broker} />
        {cfg?.armed && <div className="num mt-3 text-xs text-slate-400" data-testid="levels-arm-details">Armed {ldnTime(cfg.armed_at)} by {cfg.armed_by}. Balance {gbp(cfg.arm_balance_gbp)}, equity {gbp(cfg.arm_equity_gbp)}.</div>}
        {isAdmin ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {cfg?.armed
              ? <Button data-testid="disarm-button" variant="secondary" onClick={() => act("/levels/disarm", "Disarmed")}><Power className="mr-2 h-4 w-4" />Disarm</Button>
              : <Button data-testid="arm-button" className="bg-amber-500 text-slate-950 hover:bg-amber-400" onClick={() => act("/levels/arm", "Armed for live")}><Zap className="mr-2 h-4 w-4" />Arm live trading</Button>}
          </div>
        ) : <p className="mt-4 text-xs text-slate-500">Only an admin can arm live trading.</p>}
        <p className="mt-4 text-xs leading-relaxed text-slate-500">Live orders go out only when the MT5 account is connected and verified, the engine is armed, the market is set to live mirror and the kill switch is on. Every restart comes back disarmed.</p>
      </Panel>
      <Panel title="Kill switch" testid="kill-switch-panel" right={<Chip status={k.state || "OK"} testid="kill-state-chip" />}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Kpi label="Baseline equity" value={gbp(cfg?.arm_equity_gbp)} testid="kill-baseline" />
          <Kpi label="Current equity" value={gbp(k.current_equity)} testid="kill-current" />
          <Kpi label="Drawdown" value={k.drawdown_pct == null ? "-" : `${fmt(k.drawdown_pct)}%`} className={k.drawdown_pct > 0 ? "text-rose-400" : ""} testid="kill-drawdown" />
          <Kpi label="Limit" value={`${fmt(k.kill_loss_pct, 1)}% for ${k.kill_warning_minutes}m`} />
          <Kpi label="In warning since" value={k.warning_since ? ldnTime(k.warning_since) : "-"} testid="kill-warning-since" />
          <Kpi label="Last check" value={k.last_check ? ldnTime(k.last_check) : "-"} />
        </div>
        {isAdmin && (
          <div className="mt-4 space-y-4">
            <KillConfig kill={k} onSaved={onChange} />
            <Button data-testid="kill-button" className="w-full bg-rose-600 font-bold tracking-widest hover:bg-rose-700" onClick={() => window.confirm("Close every live position and disarm now?") && act("/levels/kill", "Kill switch fired")}>
              <ShieldAlert className="mr-2 h-4 w-4" />KILL
            </Button>
          </div>
        )}
      </Panel>
    </div>
  );
}
